/**
 * DailyTargetWheel — collapsed pill + iOS-style Popover wheel picker.
 *
 *   Collapsed (default):        Expanded (on click):
 *   ┌──────────┐                ┌──────────┐
 *   │ 20   ▾   │  ← trigger     │    15    │  ← dimmed
 *   └──────────┘                │  ▶ 20 ◀  │  ← centre selection band
 *                               │    25    │  ← dimmed
 *                               │    30    │
 *                               └──────────┘  Tap any row to commit + close.
 *                                             Scroll + close-outside also commits
 *                                             to whatever row settled in the centre.
 *
 * Smoothness rules (learnt the hard way — the previous inline picker
 * was jittery because it fought its own scrollTop):
 *   1. We only assign `scrollTop` ONCE, on mount (useLayoutEffect).
 *      Never during scroll — the browser owns scroll position after
 *      that. Fighting it caused the "stuck thinking" feel.
 *   2. `onChange` is not fired mid-scroll. We update ONLY a local
 *      `pending` value via requestAnimationFrame so React re-renders
 *      the highlight without triggering parent re-renders that would
 *      re-mount the picker.
 *   3. `scrollend` event (Chrome / Edge / newer Safari) commits `pending`
 *      to the parent, with a 120ms setTimeout fallback for browsers
 *      without native scroll-end.
 *   4. Native scroll-snap + touch drag + mouse wheel all work by
 *      default — no custom wheel handler needed.
 */
import React from "react";
import { ChevronDown } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function DailyTargetWheel({
  value,
  onChange,
  min = 5,
  max = 100,
  step = 5,
  disabled = false,
  className,
  testid = "daily-target-wheel",
}) {
  const values = React.useMemo(() => {
    const out = [];
    for (let v = min; v <= max; v += step) out.push(v);
    return out;
  }, [min, max, step]);

  const [open, setOpen] = React.useState(false);
  // Pending value while the picker is open — committed on tap, on
  // scroll-end, or on outside-click. Reset to the current prop each
  // time the popover opens so cancelling still works cleanly.
  const [pending, setPending] = React.useState(value);
  React.useEffect(() => {
    if (open) setPending(value);
  }, [open, value]);

  const commit = (v) => {
    setPending(v);
    if (v !== value) onChange?.(v);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        // Outside-click / Escape → commit whatever is currently under
        // the selection band (behaves like iOS "tap-away = save").
        if (!o && pending !== value) onChange?.(pending);
        setOpen(o);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 h-8 px-3 rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm transition-colors",
            "hover:border-slate-300 hover:bg-slate-50",
            "focus:outline-none focus:ring-2 focus:ring-emerald-500/40",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
          data-testid={testid}
          aria-label={`Realtors per day — ${value}`}
        >
          <span className="text-sm font-bold tabular-nums" data-testid={`${testid}-value`}>
            {value}
          </span>
          <ChevronDown className="h-3 w-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-24 p-0 rounded-2xl overflow-hidden"
        data-testid={`${testid}-popover`}
      >
        <WheelPicker
          values={values}
          initial={value}
          onSettle={setPending}
          onCommit={commit}
        />
      </PopoverContent>
    </Popover>
  );
}

// ---- Internal picker ------------------------------------------------------

const ROW_PX = 36;         // Row height — big enough for tap on mobile.
const VISIBLE_ROWS = 5;    // 2 dim on top, 1 centre, 2 dim on bottom.
const HEIGHT_PX = ROW_PX * VISIBLE_ROWS;

function WheelPicker({ values, initial, onSettle, onCommit }) {
  const scrollRef = React.useRef(null);
  const rafRef = React.useRef(0);
  const settleTimerRef = React.useRef(null);
  const [display, setDisplay] = React.useState(initial);

  // Set the starting scroll position ONCE, before paint, so the user
  // sees the picker land on `initial` without any visible animation.
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = values.indexOf(initial);
    if (idx >= 0) el.scrollTop = idx * ROW_PX;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On every scroll frame update the highlighted row (visual only).
  // Using rAF de-duplicates back-to-back scroll events so React only
  // re-renders once per animation frame → no jitter.
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = scrollRef.current;
      if (!el) return;
      const idx = Math.round(el.scrollTop / ROW_PX);
      const clamped = Math.max(0, Math.min(values.length - 1, idx));
      const v = values[clamped];
      setDisplay((prev) => (prev === v ? prev : v));
    });
    // Scroll-end detection: settle 120ms after the last scroll event
    // OR immediately via native `scrollend` (attached below).
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(commitSettled, 120);
  };

  const commitSettled = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / ROW_PX);
    const clamped = Math.max(0, Math.min(values.length - 1, idx));
    onSettle?.(values[clamped]);
  };

  // Native scrollend (Chrome 114+, Firefox 109+) fires precisely once
  // the scroll animation finishes. Safari falls back to the 120ms
  // setTimeout inside handleScroll.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || !("onscrollend" in el)) return undefined;
    el.addEventListener("scrollend", commitSettled);
    return () => el.removeEventListener("scrollend", commitSettled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up any lingering timers / raf handles on unmount.
  React.useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
  }, []);

  // Padding rows above / below so the FIRST and LAST values can still
  // snap to centre (otherwise the browser refuses to scroll past them
  // and the top/bottom picks feel "stuck").
  const padCount = (VISIBLE_ROWS - 1) / 2;

  return (
    <div className="relative select-none bg-white" style={{ height: HEIGHT_PX }}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll snap-y snap-mandatory no-scrollbar"
        style={{ scrollBehavior: "smooth" }}
      >
        <div aria-hidden="true" style={{ height: ROW_PX * padCount }} />
        {values.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onCommit?.(v)}
            style={{ height: ROW_PX }}
            className={cn(
              "w-full snap-center flex items-center justify-center text-lg tabular-nums transition-all duration-150",
              v === display
                ? "font-bold text-slate-900 scale-105"
                : "text-slate-400 hover:text-slate-600",
            )}
            data-testid={`daily-target-wheel-option-${v}`}
            aria-selected={v === display}
            role="option"
          >
            {v}
          </button>
        ))}
        <div aria-hidden="true" style={{ height: ROW_PX * padCount }} />
      </div>

      {/* Centre selection band — subtle rounded box behind the middle
          row, matching iOS's "hairline over + under". Pointer-events
          off so scroll gestures pass through. */}
      <div
        className="absolute inset-x-2 pointer-events-none rounded-md bg-slate-100/70 border border-slate-200"
        style={{ top: (HEIGHT_PX - ROW_PX) / 2, height: ROW_PX }}
        aria-hidden="true"
      />
      {/* Top / bottom fades for the "wheel curvature" impression. */}
      <div
        className="absolute inset-x-0 top-0 pointer-events-none bg-gradient-to-b from-white via-white/80 to-transparent"
        style={{ height: ROW_PX * padCount }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent"
        style={{ height: ROW_PX * padCount }}
        aria-hidden="true"
      />
    </div>
  );
}
